import React from 'react';
import ConnectMixin from 'app/mixins/ConnectMixin.js';
import ReactivateModal from 'app/components/Modals/ReactivateModal.jsx';

/*
 * Bringing a social back, for a list that draws inactive accounts instead of
 * filtering them out. `restoreSocial` is the way in: the account decides which
 * repair it needs, so no caller has to.
 *
 *   reactivateSocial  the account is merely switched off — flip activeFlag
 *   reconnectSocial   the system switched it off (sysActionFlag) — re-authorise
 *
 * The two doors stay exposed for a caller that has already looked — the route
 * detail's toggle raises a reconnect toast rather than acting on its own.
 *
 * Both consume an output slot, so the plan check lives here and not in the
 * call sites: a surface that wires this gets the upgrade prompt for free and
 * cannot forget it.
 *
 * The reactivate prompt is this module's own, and `onReactivate` is a
 * notification rather than a takeover: a host that needs to know which account
 * was brought back — to select it once the save lands — gets told, without
 * owning how the question is asked.
 *
 * `reconnectHandler` IS a takeover, because a reconnect leaves the page. A host
 * with unsaved work has to get it somewhere that survives the navigation first,
 * and only it knows when that is done, so it is handed the redirect to call.
 */
export default function SocialRestoreModule(Component) {

    class SocialRestoreModule extends React.Component {
        constructor(props) {
            super(props);

            this.restoreSocial = this.restoreSocial.bind(this);
            this.reactivateSocial = this.reactivateSocial.bind(this);
            this.reconnectSocial = this.reconnectSocial.bind(this);
            this.reconnectService = ConnectMixin.reconnectService.bind(this);
            this.hasOutputAvailable = this.hasOutputAvailable.bind(this);

        }

        hasOutputAvailable() {
            if (this.props.user === undefined ||
                this.props.user.perms === undefined ||
                this.props.user.perms.resources === undefined) {
                return true;
            }

            return this.props.user.perms.resources.outputsAvailable !== 0;

        }

        reactivateSocial(social) {
            if (!social) {
                return;
            }

            if (!this.hasOutputAvailable()) {
                this.props.showUpgradeModal(this.props.closeAllModals, 'socials', null, 'social_restore');
                return;
            }

            /*
             * Popped rather than closed: this prompt is often raised over the
             * connect dialog, and answering it should hand the user back to the
             * account list they were choosing from, not close the flow out from
             * under them. Where it is the only modal, popping it is closing it.
             */
            this.props.addModal(
                <ReactivateModal
                    social={social}
                    cancelAction={this.props.popModal}
                    activateSocial={() => {
                        if (this.props.onReactivate) {
                            this.props.onReactivate(social);
                        }

                        // A clone: mutating the record in the store would change
                        // what the list is drawing before the save comes back.
                        this.props.updateSocial(Object.assign({}, social, {activeFlag: 1}));
                        this.props.popModal();
                    }}/>
            );

        }

        reconnectSocial(social) {
            if (!social) {
                return;
            }

            if (!this.hasOutputAvailable()) {
                this.props.showUpgradeModal(this.props.closeAllModals, 'socials', null, 'social_restore');
                return;
            }

            let reconnect = () => {
                this.reconnectService(social.psID, social.id);
            };

            if (this.props.reconnectHandler) {
                this.props.reconnectHandler(social, reconnect);
                return;
            }

            reconnect();

        }

        /*
         * The system switched this one off, so it needs its credentials back
         * rather than its flag flipped: the server clears sysActionFlag whenever
         * activeFlag is set, and a bare reactivate would go live on dead
         * credentials until the next post knocked it off again.
         */
        restoreSocial(social) {
            if (!social) {
                return;
            }

            if (parseInt(social.sysActionFlag, 10) === 1) {
                this.reconnectSocial(social);
            } else {
                this.reactivateSocial(social);
            }

        }

        render() {
            return (
                <Component
                    {...this.props}
                    restoreSocial={this.restoreSocial}
                    reactivateSocial={this.reactivateSocial}
                    reconnectSocial={this.reconnectSocial}/>
            );
        }
    }

    SocialRestoreModule.displayName = "SocialRestoreModule";

    return SocialRestoreModule;
}
